/**
 * StatusBadge - scan/report status badge + optional icon
 *
 * @param status - 'completed' | 'failed' | 'partial' | string
 * @param showIcon - render a leading icon alongside the badge (default false)
 */

import { CheckCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';

const STATUS_MAP: Record<
  string,
  { label: string; badgeClass: string; icon: React.ElementType; iconClass: string }
> = {
  queued: {
    label: 'Queued',
    badgeClass: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    icon: Clock,
    iconClass: 'text-blue-500',
  },
  running: {
    label: 'Scanning…',
    badgeClass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    icon: Loader2,
    iconClass: 'text-emerald-500 animate-spin',
  },
  completed: {
    label: 'Completed',
    badgeClass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
  },
  failed: {
    label: 'Failed',
    badgeClass: '',
    icon: AlertTriangle,
    iconClass: 'text-red-500',
  },
  partial: {
    label: 'Partial',
    badgeClass: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    icon: Clock,
    iconClass: 'text-yellow-500',
  },
};

interface StatusBadgeProps {
  status: string;
  showIcon?: boolean;
}

export function StatusBadge({ status, showIcon = false }: StatusBadgeProps) {
  const config = STATUS_MAP[status];

  if (!config) {
    return (
      <Badge variant="outline" className="border-zinc-600 text-zinc-400 capitalize">
        {status}
      </Badge>
    );
  }

  const Icon = config.icon;

  return (
    <div className="flex items-center gap-2">
      {showIcon && <Icon className={`w-4 h-4 ${config.iconClass}`} />}
      {status === 'failed' ? (
        <Badge variant="destructive">{config.label}</Badge>
      ) : (
        <Badge variant="default" className={config.badgeClass}>
          {config.label}
        </Badge>
      )}
    </div>
  );
}
